import { Target, Mail, Network, Zap } from 'lucide-react';

export const STRATEGY_IDS = {
    BASELINE_NETWORKING: 1,
    STRATEGIC_OUTREACH: 2,
    REFERRAL_NETWORK: 3,
    ATS_FIRST: 4
};

export const STRATEGY_CATALOG = [
    {
        id: STRATEGY_IDS.BASELINE_NETWORKING,
        title: 'Strategy #1: Baseline Networking',
        icon: Target,
        color: '#4CAF50',
        description: 'Build trust first, then ask for referral when rapport is strong',
        estimatedTime: '3-5 days',
        successRate: 'Medium',
        bestFor: 'No clear open role yet',
        urgencyLevel: 'Low',
        switchTriggers: [
            'Open role appears',
            'Signal strength rises above threshold',
            'ATS-first early discovery detected'
        ],
        taskTemplate: 'relationship_building',
        subStrategies: [
            {
                key: 'warm-research-nurture',
                name: 'Warm Research & Nurture',
                whenToUse: 'No role open, but company is strategically important.',
                actions: [
                    'Map hiring manager and recruiter personas',
                    'Engage on LinkedIn with role-relevant insights',
                    'Start a soft intro message without referral ask'
                ]
            },
            {
                key: 'value-led-intro',
                name: 'Value-Led Intro',
                whenToUse: 'Contact accepted but no active req yet.',
                actions: [
                    'Send concise credibility + domain fit note',
                    'Share one relevant project or outcome',
                    'Request light guidance before referral'
                ]
            },
            {
                key: 'monitor-and-pivot',
                name: 'Monitor & Pivot',
                whenToUse: 'Signal is warming and role is likely soon.',
                actions: [
                    'Track hiring signals every 24-48h',
                    'Prepare role-tailored resume variant',
                    'Pivot to Strategic Outreach once role opens'
                ]
            }
        ],
        steps: [
            'Identify recruiters and hiring managers for the company',
            'Send warm connection request with role context',
            'Build trust through thoughtful engagement',
            'Ask referral only after response and rapport'
        ]
    },
    {
        id: STRATEGY_IDS.STRATEGIC_OUTREACH,
        title: 'Strategy #2: Strategic Outreach',
        icon: Mail,
        color: '#2196F3',
        description: 'Apply + direct recruiter outreach + timed follow up',
        estimatedTime: '1-2 hours',
        successRate: 'Medium-High',
        bestFor: 'Open role with normal urgency',
        urgencyLevel: 'Medium',
        switchTriggers: [
            'Role closes or loses momentum',
            'ATS-first role discovered',
            'Referral path becomes available'
        ],
        taskTemplate: 'direct_outreach',
        subStrategies: [
            {
                key: 'apply-then-message',
                name: 'Apply Then Message',
                whenToUse: 'Role open, competition moderate.',
                actions: [
                    'Apply with tailored resume first',
                    'Send same-day recruiter message',
                    'Follow up in 48 hours'
                ]
            },
            {
                key: 'manager-first-outreach',
                name: 'Manager-First Outreach',
                whenToUse: 'Hiring manager identified clearly.',
                actions: [
                    'Message manager with role-fit proof',
                    'Reference specific business problem',
                    'Loop recruiter in after manager engagement'
                ]
            },
            {
                key: 'signal-boosted-outreach',
                name: 'Signal-Boosted Outreach',
                whenToUse: 'Open role plus positive hiring signals.',
                actions: [
                    'Prioritize high-signal departments',
                    'Use urgency framing in outreach',
                    'Escalate with short-cycle follow-up'
                ]
            }
        ],
        steps: [
            'Tailor resume to role requirements',
            'Apply through official channel',
            'Send recruiter/hiring manager outreach',
            'Follow up in 48 hours with concise value reminder'
        ]
    },
    {
        id: STRATEGY_IDS.REFERRAL_NETWORK,
        title: 'Strategy #3: Referral Network',
        icon: Network,
        color: '#9C27B0',
        description: 'Activate internal network quickly for referral leverage',
        estimatedTime: '2-4 days',
        successRate: 'High',
        bestFor: 'Fresh job posting or strong hiring signal',
        urgencyLevel: 'High',
        switchTriggers: [
            'Referral path stalls beyond threshold',
            'No open role and signal cools down',
            'ATS-first role appears'
        ],
        taskTemplate: 'referral_activation',
        subStrategies: [
            {
                key: 'advocate-seeding',
                name: 'Advocate Seeding',
                whenToUse: 'Early role with potential internal supporters.',
                actions: [
                    'Identify 3-5 internal potential advocates',
                    'Send personalized connection requests day 1',
                    'Follow accepted contacts with value context day 2'
                ]
            },
            {
                key: 'referral-with-proof',
                name: 'Referral With Proof',
                whenToUse: 'Advocate engaged but referral not requested yet.',
                actions: [
                    'Share concise role-fit bullets',
                    'Request referral directly and politely',
                    'Mention referral context in recruiter outreach'
                ]
            },
            {
                key: 'fallback-to-outreach',
                name: 'Fallback To Outreach',
                whenToUse: 'Referral path not moving after 3-5 days.',
                actions: [
                    'Switch to Strategic Outreach cadence',
                    'Keep advocate warm for second attempt',
                    'Re-evaluate weekly on signal refresh'
                ]
            }
        ],
        steps: [
            'Find likely internal advocates at the company',
            'Send personalized connection requests on day 1',
            'Follow up with accepted contacts on day 2',
            'Ask for referral and mention referrer in recruiter outreach'
        ]
    },
    {
        id: STRATEGY_IDS.ATS_FIRST,
        title: 'Strategy #4: ATS-First Advantage',
        icon: Zap,
        color: '#F59E0B',
        description: 'Move same day when role appears early in ATS',
        estimatedTime: '30-90 minutes',
        successRate: 'Very High',
        bestFor: 'ATS-first early discovery',
        urgencyLevel: 'Very High',
        switchTriggers: [
            'Role no longer active',
            'No response after rapid follow-up window',
            'ATS freshness advantage expires'
        ],
        taskTemplate: 'ats_sprint',
        subStrategies: [
            {
                key: 'same-day-strike',
                name: 'Same-Day Strike',
                whenToUse: 'Role surfaced via ATS before broad job boards.',
                actions: [
                    'Apply immediately with role-tailored resume',
                    'Send same-day message to recruiter/manager',
                    'Flag as top priority in daily mission'
                ]
            },
            {
                key: '24h-escalation',
                name: '24h Escalation',
                whenToUse: 'No response in first 24 hours.',
                actions: [
                    'Send concise escalation follow-up',
                    'Add alternate contact from same org',
                    'Reinforce role-fit in one paragraph'
                ]
            },
            {
                key: 'fast-fallback',
                name: 'Fast Fallback To Strategic Outreach',
                whenToUse: 'ATS edge is gone but role remains open.',
                actions: [
                    'Continue via Strategic Outreach template',
                    'Preserve urgency but reduce cadence',
                    'Track signal delta every 48 hours'
                ]
            }
        ],
        steps: [
            'Apply immediately while role is fresh',
            'Send same-day message to recruiter/hiring manager',
            'Escalate with a fast follow-up in 24-48 hours',
            'Fallback to Strategic Outreach if no momentum'
        ]
    }
];

export const STRATEGY_NAME_BY_ID = {
    [STRATEGY_IDS.BASELINE_NETWORKING]: 'Baseline Networking',
    [STRATEGY_IDS.STRATEGIC_OUTREACH]: 'Strategic Outreach',
    [STRATEGY_IDS.REFERRAL_NETWORK]: 'Referral Network',
    [STRATEGY_IDS.ATS_FIRST]: 'ATS-First Advantage'
};
